import { pdfjs } from "react-pdf";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Threshold for minimum characters per page to consider PDF as text-based (non-scanned)
 * Scanned PDFs typically have very few or no extractable text characters
 */
const MIN_CHARACTERS_PER_PAGE = 20;

/**
 * Validates if a PDF file is text-based (non-scanned) by extracting text and checking character count
 * @param file - The PDF file to validate
 * @param minCharsPerPage - Minimum characters per page threshold (default: 20)
 * @returns Promise that resolves to true if PDF is text-based, false if scanned
 * @throws Error if PDF cannot be read or processed
 */
export const validatePdfIsTextBased = async (
  file: File,
  minCharsPerPage: number = MIN_CHARACTERS_PER_PAGE
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== "application/pdf") {
      reject(new Error("Invalid PDF file"));
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        const numPages = pdf.numPages;

        // Extract text from all pages
        let totalCharacters = 0;
        const textPromises: Promise<string>[] = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item: any) => item.str || "")
            .join(" ");
          totalCharacters += pageText.trim().length;
        }

        // Calculate minimum required characters
        const minRequiredCharacters = numPages * minCharsPerPage;

        // PDF is considered text-based if it has at least the minimum characters
        const isTextBased = totalCharacters >= minRequiredCharacters;

        resolve(isTextBased);
      } catch (error) {
        console.error("Error validating PDF:", error);
        reject(new Error("Failed to validate PDF file"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
};
